import React, { useContext, useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import Loader from './Loader';
import { db } from './firebase-config';
import { useUser } from '../context/UserProvider';
import { DataContext, Prize } from '../context/DataContext';
import { useAppConfig } from '../context/AppConfigContext';
import { APP_DEFAULTS } from '../config';
import { isCampaignEnded } from '../utils/campaign';
import { getUserSpinCountByEmail } from '../services/userStats';

const SubscriptionForm: React.FC = () => {
  const { user, setUser } = useUser();
  const [fullName, setFullName] = useState(user.fullName);
  const [email, setEmail] = useState(user.email);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const navigate = useNavigate();
  const { setData } = useContext(DataContext);
  const { config } = useAppConfig();

  const loaderDelay = config.loaderDelay ?? APP_DEFAULTS.loaderDelay;
  const campaignIsEnded = isCampaignEnded(config.campaignEndsAt);

  const campaignBlockMessage = (() => {
    if (campaignIsEnded) return 'This campaign has already ended.';
    if (!config.campaignEnabled) return 'This campaign is currently disabled.';
    return null;
  })();

  useEffect(() => {
    const fetchPrizes = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, APP_DEFAULTS.firestoreCollection));
        const dataList = querySnapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        })) as Prize[];
        setData(dataList);
      } catch (error) {
        console.error('Error fetching prizes:', error);
        setErrorMsg('Failed to load prizes. Please try again.');
      }
    };

    fetchPrizes();
  }, [setData]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (campaignBlockMessage) {
      setErrorMsg(campaignBlockMessage);
      return;
    }

    if (!fullName.trim() || !email.trim()) {
      setErrorMsg('Veuillez remplir tous les champs.');
      return;
    }

    const normalizedFullName = fullName.trim().replace(/\s+/g, ' ');
    const normalizedEmail = email.trim().toLowerCase();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(normalizedEmail)) {
      setErrorMsg('Veuillez saisir une adresse email valide.');
      return;
    }

    setLoading(true);
    setErrorMsg(null);

    try {
      const remoteSpinCount =
        config.maxSpinsPerUser > 0
          ? await getUserSpinCountByEmail(normalizedEmail)
          : 0;

      if (config.maxSpinsPerUser > 0 && remoteSpinCount >= config.maxSpinsPerUser) {
        setLoading(false);
        setErrorMsg('Cet email a deja atteint le nombre maximum de participations.');
        return;
      }

      setUser({
        fullName: normalizedFullName,
        email: normalizedEmail,
        docId: '',
      });

      setTimeout(() => {
        setLoading(false);
        navigate('/wheel-of-fortune');
      }, loaderDelay);
    } catch (error) {
      console.error('Failed to validate participant eligibility:', error);
      setLoading(false);
      setErrorMsg("Impossible de verifier cet email maintenant. Veuillez reessayer.");
    }
  };

  const inputClass =
    'mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-yellow-300 focus:border-yellow-500 sm:text-sm text-gray-900';

  const labelClass = 'block text-gray-300 font-medium text-left text-sm mb-1';

  return (
    <div className="flex justify-center items-center min-h-screen bg-black bg-opacity-0 animate-fade animate-duration-[3000ms] animate-ease-out">
      <Loader show={loading} />
      <div className="w-full max-w-md p-6 bg-opacity-100 shadow-md rounded-md">
        {errorMsg && (
          <div className="mb-4 rounded-md border border-red-400/40 bg-red-500/10 px-4 py-3 text-center">
            <p className="text-sm text-red-300">{errorMsg}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          <div className="mb-4">
            <label htmlFor="full-name" className={labelClass}>Full name</label>
            <input
              type="text"
              id="full-name"
              className={inputClass}
              required
              autoComplete="name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
            />
          </div>

          <div className="mb-6">
            <label htmlFor="email" className={labelClass}>Email</label>
            <input
              type="email"
              id="email"
              className={inputClass}
              required
              inputMode="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div className="btn-margin">
            <button
              type="submit"
              className="contactbutton"
              disabled={Boolean(campaignBlockMessage) || loading}
            >
              Continuer
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SubscriptionForm;
